import React from 'react';
import Spinner from './Spinner';

/**
 * Tailwind Button — replaces MUI Button.
 * variant: 'primary' | 'outline' | 'ghost' | 'danger' | 'dangerOutline'
 * Also accepts MUI-style props (variant="contained|outlined|text", color="primary|error|success")
 * so migrated call-sites can keep familiar names.
 */
const SIZES = {
  xs: 'h-7 px-2.5 text-xs',
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
};

function resolveVariant(variant, color) {
  if (variant === 'contained') {
    return color === 'error' ? 'danger' : 'primary';
  }
  if (variant === 'outlined') {
    return color === 'error' ? 'dangerOutline' : 'outline';
  }
  if (variant === 'text') return 'ghost';
  return variant;
}

const VARIANTS = {
  primary:
    'bg-[#0061B2] text-white shadow-sm hover:bg-[#004d8c] disabled:bg-[#9ec3e0] disabled:text-white',
  outline:
    'border border-[#d0d5dd] bg-white text-[#212B36] hover:bg-[#FAFBFE] disabled:text-[#9aa3ab]',
  ghost:
    'text-[#0061B2] hover:bg-[#eaf3fb] disabled:text-[#9aa3ab]',
  danger:
    'bg-[#EF4444] text-white shadow-sm hover:bg-[#DC2626] disabled:bg-[#f5b5b5]',
  dangerOutline:
    'border border-[#EF4444]/40 bg-white text-[#EF4444] hover:bg-[#FEF2F2] disabled:text-[#f5b5b5]',
};

const Button = React.forwardRef(
  (
    {
      children,
      variant = 'primary',
      color,
      size = 'md',
      startIcon = null,
      endIcon = null,
      loading = false,
      fullWidth = false,
      disabled = false,
      type = 'button',
      className = '',
      ...rest
    },
    ref
  ) => {
    const v = VARIANTS[resolveVariant(variant, color)] || VARIANTS.primary;
    const s = SIZES[size === 'small' ? 'sm' : size === 'large' ? 'lg' : size] || SIZES.md;
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        className={`inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0061B2]/30 disabled:cursor-not-allowed ${v} ${s} ${
          fullWidth ? 'w-full' : ''
        } ${className}`}
        {...rest}
      >
        {loading ? <Spinner size={16} className="text-current" /> : startIcon}
        {children}
        {endIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
export default Button;
